import React, { forwardRef, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useToast } from '../../hooks/usetoast';
import { WaitingLoader } from '../../hooks/buttonLoad';
import ReactPatternLock from 'react-pattern-lock';
import user from '../../assets/images/icons/user.svg';
import { RotateCcw, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAddSubAdmin } from './api/index';
import { CircularProgress } from '@mui/material';
import PermissionDenied from '../common/permissionDenied';
import { useCheckAdminAccess } from '../WhiteIp/api/whiteipapi';
import { logout } from '../../hooks/logout';
import eyeslash from '../../assets/images/icons/eye-slash.svg'
import eye from '../../assets/images/icons/eye.svg'

const allOptions = [
    { module: "Login History", link: "/login-history", icon: "clock-outline" },
    { module: "User Management", link: "/user-management", icon: "clock-outline" },
    { module: "Homepage Management", link: "", icon: "block-outline" },
    { module: "Send Management", link: "", icon: "clock-outline" },
    { module: "Receive Management", link: "", icon: "clock-outline" },
    { module: "Fee Management", link: "/fee-management", icon: "clock-outline" },
    { module: "Chart currency", link: "/Chartcurrency", icon: "clock-outline" },
    { module: "Core wallet", link: "/corewalletbalance", icon: "clock-outline" },
    { module: "Referral Management", link: "", icon: "clock-outline" },
    { module: "History", link: "", icon: "clock-outline" },
];


const schema = yup.object().shape({
    userName: yup
        .string()
        .min(3, 'userName must be at least 3 characters')
        .max(15, 'userName must be at most 15 characters')
        .required('userName is required')
        .trim()
        .matches(/^[a-zA-Z0-9\s]*$/, 'userName must contain only alphanumeric characters and spaces'),
    email: yup.string().required('Email is required').email('Invalid Email'),
    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
    pattern: yup.array().min(1, 'Draw a pattern').required('Pattern is required').min(4, 'Connect at least 4 dots'),
    permission: yup.array().min(1, 'Select at least one permission').required('Permission is Required')
});

const defaultValues = {
    userName: '',
    email: '',
    password: '',
    pattern: [],
    permission: []
};

const PatternLock = forwardRef((props, ref) => (
    <ReactPatternLock {...props} ref={ref} />
));



const AddSubAdmin = () => {
    const { control, handleSubmit, formState: { errors }, reset, setValue, setError } = useForm({
        mode: 'onChange',
        defaultValues,
        resolver: yupResolver(schema),
    });
    const [loading, setLoading] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const navigate = useNavigate();
    const subadminmutation = useAddSubAdmin();
    const toast = useToast();

    const clearPattern = () => {
        setValue('pattern', []);
        setError('pattern', { type: 'required', message: 'Draw a pattern' });
    };


    const [inputType, setInputType] = useState(true);
    const toggleInputType = () => {
        setInputType(prevType => !prevType);
    };

    const handleOptionChange = (option, checked) => {
        setSelectedOptions((prevSelectedOptions) => {
            let updatedSelectedOptions = [...prevSelectedOptions];
            const existingOptionIndex = updatedSelectedOptions.findIndex(
                (item) => item.title === option.module
            );

            if (existingOptionIndex !== -1) {
                if (!checked) {
                    updatedSelectedOptions.splice(existingOptionIndex, 1);
                }
            } else if (checked) {
                updatedSelectedOptions.push({
                    title: option.module,
                    icon: option.icon,
                    link: option.link,
                    status: checked,
                });
            }

            setValue('permission', updatedSelectedOptions);
            if (updatedSelectedOptions.length > 0) {
                setError('permission', { type: 'manual', message: '' });
            } else {
                setError('permission', { type: 'manual', message: 'Select at least one permission' });
            }
            return updatedSelectedOptions;
        });
    };

    const onSubmit = async (data) => {
        if (selectedOptions.length < 1) {
            setError('permission', { type: 'manual', message: 'Select at least one permission' });
            return;
        }

        setLoading(true);
        const pattern = data.pattern.map((e) => e + 1).join('');
        const obj = {
            userName: data.userName,
            email: data.email,
            password: data.password,
            pattern: pattern,
            permission: data.permission
        };

        const res = await subadminmutation.mutateAsync(obj);
        if (res?.status) {
            reset();
            setLoading(false);
            setSelectedOptions([]);
            toast.success("Added successfully");
            navigate('/SubAdmin');
        } else {
            toast.error(res?.message);
            setLoading(false);
        }
    };

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'AddSubAdmin' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };

    let content;

    if (loading) {
        content = <WaitingLoader />;
    } else if (access) {
        content = (
            <>
                <div className='d-flex flextwrap justify-content-between'>
                    <h6>
                        <ChevronLeft size={20} onClick={() => navigate('/SubAdmin')} style={{ cursor: "pointer" }} />
                        Staff Login Details
                    </h6>
                </div>
                <h5 className="mt-2">Add Staff Login</h5>
                <span>Info of the Staff Login Details</span>

                <div className="ad_pg_bx bg-white p-4 px-lg-5 mt-3">
                    <div className="forms_ip_blk">
                        <div className="row justify-content-center">
                            <div className="col-sm-12 col-md-4 col-lg-4">
                                <div className="input-group mb-3">
                                    <span className="input-group-icon">
                                        <img src={user} className="img-fluid" alt="User" />
                                    </span>
                                    <Controller
                                        name="userName"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="text"
                                                autoFocus
                                                disabled={subadminmutation.isLoading}
                                                className="form-control"
                                                placeholder="Enter User Name"
                                                autoComplete="off"
                                            />
                                        )}
                                    />
                                </div>
                                {errors.userName && <p className="errMsg">* {errors.userName.message}</p>}

                                <div className="input-group mb-3">
                                    <span className="input-group-icon">
                                        <img src={user} className="img-fluid" alt="User" />
                                    </span>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="text"
                                                disabled={subadminmutation.isLoading}
                                                className="form-control"
                                                placeholder="Enter Email Address"
                                                autoComplete="off"
                                            />
                                        )}
                                    />
                                </div>
                                {errors.email && <p className="errMsg">* {errors.email.message}</p>}

                                <div className="input-group mb-3">
                                    <span className="input-group-icon">
                                        <img src={user} className="img-fluid" alt="User" />
                                    </span>
                                    <Controller
                                        name="password"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type={inputType ? "password" : "text"}
                                                disabled={subadminmutation.isLoading}
                                                className="form-control"
                                                placeholder="Enter Password"
                                                autoComplete="new-password"
                                            />
                                        )}
                                    />
                                    <button
                                        type="button"
                                        onClick={toggleInputType}
                                        className={`btn p-0 input-group-icon border-0 shwPwd ${inputType ? "" : "show-eye"}`.trim()}
                                    >
                                        <img src={inputType ? eyeslash : eye} className="img-fluid" alt={inputType ? "eye-slash" : "eye"} />
                                    </button>
                                </div>

                                <div>
                                    <label className="mb-8" htmlFor="modules">Modules:</label>
                                    <div className="mt-3">
                                        {allOptions.map((option) => (
                                            <div key={option.module} className="mb-8 d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    id={option.module}
                                                    name={option.module}
                                                    checked={!!selectedOptions.find(item => item.title === option.module)}
                                                    onChange={(e) => handleOptionChange(option, e.target.checked)}
                                                    style={{ marginRight: "10px" }}
                                                />
                                                <label htmlFor={option.module} style={{ fontWeight: "bold" }}>{option.module}</label>
                                            </div>
                                        ))}
                                        {errors?.permission?.message && (
                                            <div style={{ color: "red", fontSize: "12px" }}>{errors.permission.message}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex justify-content-center ml-5">
                                    <button type="button" className="btn btn-outline-link p-0" onClick={clearPattern}>
                                        <RotateCcw size={20} style={{ marginLeft: "130px" }} />
                                    </button>
                                </div>

                                <div className="pdLock pt-2 pb-3 text-center">
                                    <Controller
                                        name="pattern"
                                        control={control}
                                        render={({ ref, field }) => (
                                            <PatternLock
                                                {...field}
                                                ref={ref}
                                                width={300}
                                                disabled={subadminmutation.isLoading}
                                                size={3}
                                                path={field.value}
                                                onFinish={() => { }}
                                            />
                                        )}
                                    />
                                </div>
                                {errors.pattern && <p className="errMsg">* {errors.pattern.message}</p>}

                                <div className="d-grid crPgbtn pt-3 text-center">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-sm"
                                        style={{
                                            padding: "4px 8px",
                                            fontSize: "12px",
                                            width: "60px",
                                            height: "30px",
                                            display: "block",
                                            margin: "0 auto",
                                        }}
                                        disabled={subadminmutation.isLoading}
                                        onClick={handleSubmit(onSubmit)}
                                    >
                                        {subadminmutation.isLoading ? (
                                            <CircularProgress size={12} style={{ color: "#fff" }} />
                                        ) : (
                                            "Submit"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

};

export default AddSubAdmin;